import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthProvider";

export default function TickerSearchBox({
  className = "",
  placeholder = "Search kode saham (AHAP, BBCA...)",
}) {
  const nav = useNavigate();
  const { token } = useAuth() || {};

  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);

  const boxRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const headers = useMemo(() => {
    const h = {};
    if (token) h["Authorization"] = `Bearer ${token}`;
    return h;
  }, [token]);

  const close = () => {
    setOpen(false);
    setActiveIdx(-1);
  };

  const goTicker = (code) => {
    const c = String(code || "").trim().toUpperCase();
    if (!c) return;
    close();
    nav(`/stocks/${encodeURIComponent(c)}`);
  };

  const goTopHit = () => {
    // prioritas: item yang lagi di-highlight
    if (activeIdx >= 0 && activeIdx < items.length) {
      const it = items[activeIdx];
      return goTicker(it?.kode_saham);
    }
    // fallback: kalau ticker valid, langsung buka
    const s = (q || "").trim().toUpperCase();
    if (s && /^[A-Z0-9._-]{2,12}$/.test(s)) return goTicker(s);
  };

  const fetchTickers = async (text) => {
    const qq = (text || "").trim();
    setErr("");

    if (!qq) {
      setItems([]);
      setLoading(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    try {
      // Pakai endpoint global yang sudah ada, tapi kita hanya ambil group stocks
      const r = await fetch(`/api/search/global?q=${encodeURIComponent(qq)}&limit=8`, {
        headers,
        signal: ctrl.signal,
      });
      const js = await r.json();
      if (!r.ok) throw new Error(js?.detail || "Search failed");

      const stocks = js?.groups?.stocks || [];
      setItems(stocks);
      setOpen(true);
      setActiveIdx(stocks.length ? 0 : -1);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setErr(e?.message || "Error");
      setItems([]);
      setOpen(true);
      setActiveIdx(-1);
    } finally {
      setLoading(false);
    }
  };

  // Debounce input
  useEffect(() => {
    clearTimeout(debounceRef.current);
    const qq = (q || "").trim();
    if (!qq) {
      setItems([]);
      setLoading(false);
      setErr("");
      return;
    }
    debounceRef.current = setTimeout(() => fetchTickers(qq), 180);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, token]);

  // click outside
  useEffect(() => {
    const onDown = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) close();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <div className="flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-900 px-3 h-9">
        <span className="text-gray-400 text-sm">🔎</span>

        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              goTopHit();
            }
            if (e.key === "Escape") close();

            if (e.key === "ArrowDown") {
              e.preventDefault();
              if (!items.length) return;
              setActiveIdx((p) => (p < items.length - 1 ? p + 1 : 0));
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              if (!items.length) return;
              setActiveIdx((p) => (p > 0 ? p - 1 : items.length - 1));
            }
          }}
          placeholder={placeholder}
          className="w-full bg-transparent outline-none text-gray-100 placeholder:text-gray-500 text-sm"
        />

        {loading ? (
          <div className="text-[12px] text-gray-400">Loading…</div>
        ) : q ? (
          <button
            onClick={() => {
              setQ("");
              setItems([]);
              setErr("");
              close();
            }}
            className="text-gray-400 hover:text-gray-200 text-[12px]"
            title="Clear"
          >
            ✕
          </button>
        ) : null}
      </div>

      {/* Dropdown */}
      {open && q.trim().length > 0 ? (
        <div className="absolute z-50 mt-2 w-full rounded-xl border border-gray-700 bg-gray-950 shadow-xl overflow-hidden">
          {err ? (
            <div className="p-3 text-sm text-red-300">{err}</div>
          ) : !loading && !items.length ? (
            <div className="p-3 text-sm text-gray-400">Tidak ada hasil.</div>
          ) : (
            <div className="max-h-[60vh] overflow-auto p-1">
              {items.map((it, idx) => {
                const active = idx === activeIdx;
                return (
                  <button
                    key={`${it.kode_saham}-${idx}`}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => goTicker(it.kode_saham)}
                    className={`w-full text-left px-3 py-2 rounded-lg ${
                      active ? "bg-gray-900" : "hover:bg-gray-900"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-100">{it.kode_saham}</div>
                        {it.nama ? (
                          <div className="text-[12px] text-gray-400 truncate">{it.nama}</div>
                        ) : null}
                      </div>
                      <span className="text-[11px] px-2 py-0.5 rounded bg-gray-800 text-gray-300">
                        Saham
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
